import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";

const API_URL = process.env.REACT_APP_API_URL;

function SelfGifts() {
  const navigate = useNavigate();
  const [associateIds, setAssociateIds] = useState([]);
  const [plotSize, setPlotSize] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [associateOptions, setAssociateOptions] = useState([]);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchAssociates = async () => {
      try {
        const res = await fetch(`${API_URL}/gift-self-associate-list`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();
        if (res.ok && data.status === "1") {
          setAssociates(data.data || []);
          const formatted = (data.data || []).map((a) => ({
            value: a.id,
            label: a.username ? `${a.username} - ${a.mobile}` : a.mobile,
            originalData: a,
          }));
          setAssociateOptions(formatted);
        } else {
          console.error("Failed to fetch associates:", data.message);
        }
      } catch (err) {
        console.error("Error fetching associates:", err);
      }
    };

    fetchAssociates();
  }, [token]);

  const handlePlotSizeChange = (e) => {
    let value = e.target.value;
    if (/^\d*$/.test(value)) {
      if (value === "") {
        setPlotSize("");
        return;
      }
      if (/^0/.test(value)) return;
      setPlotSize(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let tempErrors = {};
    if (associateIds.length === 0)
      tempErrors.associateIds = "Please select at least one associate";
    if (!plotSize || parseInt(plotSize) === 0)
      tempErrors.plot_size = "Please enter plot size in SQYD";

    if (Object.keys(tempErrors).length > 0) {
      setErrors(tempErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        associateIds: associateIds.map((a) => a.value),
        buysqrt: parseInt(plotSize),
      };

      const response = await fetch(`${API_URL}/gift-self-to-associates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status === "0") {
        Swal.fire({
          icon: "error",
          title: "Assign Gifts Failed",
          text: result.message || "",
        });
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: result.message || "Assign Gifts Successfully!",
        timer: 2000,
        showConfirmButton: false,
      });

      setErrors({});
      setAssociateIds([]);
      setPlotSize("");

      setTimeout(() => {
        navigate("/gift-self-associate-list");
      }, 2000);
    } catch (err) {
      console.error("Error assigning gifts:", err);
      setErrors({ submit: err.message || "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  // custom styles for green color
  const customStyles = {
    control: (base) => ({
      ...base,
      borderColor: errors.associateIds ? "#dc3545" : base.borderColor,
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "green",
      color: "white",
      borderRadius: "8px",
      padding: "2px 6px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      fontWeight: "500",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": {
        backgroundColor: "darkgreen",
        color: "white",
      },
    }),
  };

  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                flexShrink: 0,
              }}
            >
              🎁
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                Assign Self Gifts to Associates
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Select associates and enter plot size to assign gifts
              </p>
            </div>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background:
                  "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            <div style={{ padding: "30px" }}>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3" controlId="formAssociateIds">
                      <Form.Label style={{ fontWeight: "600", color: "#1e3a5f" }}>
                        Select Associates{" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Select
                        isMulti
                        options={associateOptions}
                        value={associateIds}
                        onChange={setAssociateIds}
                        styles={customStyles}
                        placeholder="Select associates..."
                        isDisabled={
                          loading || associateOptions.length === 0
                        }
                      />
                      {errors.associateIds && (
                        <div className="invalid-feedback d-block">
                          {errors.associateIds}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group className="mb-3" controlId="formPlotSize">
                      <Form.Label style={{ fontWeight: "600", color: "#1e3a5f" }}>
                        Enter Plot Size in (SQYD){" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={plotSize}
                        onChange={handlePlotSizeChange}
                        isInvalid={!!errors.plot_size}
                        placeholder="Enter Plot Size in (SQYD) Like 50"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.plot_size}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Submit Button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingTop: "20px",
                    borderTop: "1.5px dashed #e2e8f0",
                    marginTop: "10px",
                  }}
                >
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: "13px 42px",
                      borderRadius: "12px",
                      border: "none",
                      background: loading
                        ? "#cbd5e1"
                        : "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      color: "#ffffff",
                      fontSize: "14.5px",
                      fontWeight: "800",
                      letterSpacing: "0.5px",
                      cursor: loading ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: loading
                        ? "none"
                        : "0 8px 24px rgba(30, 87, 153, 0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      minWidth: "220px",
                    }}
                  >
                    {loading ? (
                      <>
                        <span
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid rgba(255,255,255,0.3)",
                            borderTop: "2px solid #ffffff",
                            borderRadius: "50%",
                            display: "inline-block",
                            animation: "spin 0.8s linear infinite",
                          }}
                        />
                        Assigning...
                      </>
                    ) : (
                      "Gift To Associate"
                    )}
                  </button>
                </div>

                {/* Footer Note */}
                <div
                  style={{
                    marginTop: "20px",
                    paddingTop: "20px",
                    borderTop: "1.5px dashed #e2e8f0",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12.5px",
                      color: "#94a3b8",
                      fontWeight: "500",
                    }}
                  >
                    🔒 All fields marked with{" "}
                    <span style={{ color: "#dc2626" }}>*</span> are required
                  </p>
                </div>
              </Form>
            </div>
          </div>

          {/* Footer Copyright */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default SelfGifts;